import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Navigation, Wand2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { transportRoutesApi, tripsApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  TransportTrip,
  TripDirection,
  TripGenerationResult,
  TripStatus,
} from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import { Blockers, TripStatusBadge, delayText, formatTime, humanise, today } from './shared';

const STATUSES: { value: TripStatus; label: string }[] = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'BOARDING', label: 'Boarding' },
  { value: 'IN_PROGRESS', label: 'Under way' },
  { value: 'DELAYED', label: 'Delayed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const DIRECTIONS: { value: TripDirection; label: string }[] = [
  { value: 'PICKUP', label: 'Pickup' },
  { value: 'DROPOFF', label: 'Drop-off' },
];

/**
 * Every run of every route — the history, not the day board.
 *
 * <p>Daily Operations is where a dispatcher lives; this list is where "did the
 * bus run on the 4th" gets answered. Generation is a button rather than a
 * scheduler, matching every other sweep in this system — idempotent, and the
 * routes it could not fill come back with their reasons, in the server's
 * words, because a silent skip is a bus that silently never came.
 */
export function TripsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const table = useTableQuery('tripDate,desc');
  const manages = can(PERMISSIONS.TRANSPORT_TRIP_MANAGE);
  const viewsRoutes = can(PERMISSIONS.TRANSPORT_ROUTE_VIEW);

  const [status, setStatus] = useState<TripStatus | ''>('');
  const [direction, setDirection] = useState<TripDirection | ''>('');
  const [routeId, setRouteId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateDate, setGenerateDate] = useState(today());
  const [generation, setGeneration] = useState<TripGenerationResult | null>(null);

  const params = {
    ...table.params,
    ...(status !== '' ? { status } : {}),
    ...(direction !== '' ? { direction } : {}),
    ...(routeId !== '' ? { routeId } : {}),
    ...(from !== '' ? { from } : {}),
    ...(to !== '' ? { to } : {}),
  };

  const trips = useQuery({
    queryKey: queryKeys.trips.list(params),
    queryFn: () => tripsApi.list(params),
  });

  const routes = useQuery({
    queryKey: queryKeys.transportRoutes.list({ picker: true }),
    queryFn: () => transportRoutesApi.list({ page: 0, size: 200 }),
    enabled: viewsRoutes,
    staleTime: 5 * 60_000,
  });

  const generate = useMutation({
    mutationFn: () => tripsApi.generate(generateDate === '' ? undefined : generateDate),
    onSuccess: (result) => {
      setGeneration(result);
      toast.success(
        result.created === 0
          ? `Nothing new to schedule for ${formatDate(result.date)}`
          : `${result.created} trip${result.created === 1 ? '' : 's'} created for ${formatDate(result.date)}`,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.trips.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<TransportTrip>[] = [
    {
      key: 'trip',
      header: 'Trip',
      sortKey: 'tripDate',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.tripDetail(row.id)}
            onClick={(event) => event.stopPropagation()}
            className="block font-mono text-[12px] font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.tripNumber}
          </Link>
          <span className="block text-[11px] text-subtle">{formatDate(row.tripDate)}</span>
        </span>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{row.routeName}</span>
          <span className="block text-[11px] text-subtle">{humanise(row.direction)}</span>
        </span>
      ),
    },
    {
      key: 'planned',
      header: 'Planned',
      sortKey: 'plannedStartTime',
      render: (row) => (
        <span className="tabular-nums text-[13px] text-ink">
          {formatTime(row.plannedStartTime)} – {formatTime(row.plannedEndTime)}
        </span>
      ),
    },
    {
      key: 'crew',
      header: 'Vehicle & crew',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.vehicleName)}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.driverName)}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span>
          <TripStatusBadge status={row.status} />
          {row.status === 'DELAYED' && (
            <span className="mt-0.5 block text-[11px] text-[color:var(--sms-warning)]">
              {delayText(row.delayMinutes)}
              {row.delayReason !== undefined && ` — ${row.delayReason}`}
            </span>
          )}
          {row.status === 'CANCELLED' && row.cancellationReason !== undefined && (
            <span
              className="mt-0.5 block max-w-[16rem] truncate text-[11px] text-subtle"
              title={row.cancellationReason}
            >
              {row.cancellationReason}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Trips"
        description="Every run of every route, and what became of each."
        icon={<Navigation className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? (
            <Button
              onClick={() => {
                setGeneration(null);
                setGenerateDate(today());
                setGenerateOpen(true);
              }}
            >
              <Wand2 className="h-4 w-4" aria-hidden="true" />
              Generate a day
            </Button>
          ) : undefined
        }
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={STATUSES}
            />
            <FilterSelect
              label="Direction"
              value={direction}
              onChange={(value) => {
                setDirection(value);
                table.setPage(0);
              }}
              options={DIRECTIONS}
            />
            {viewsRoutes && (
              <FilterSelect
                label="Route"
                value={routeId}
                onChange={(value) => {
                  setRouteId(value);
                  table.setPage(0);
                }}
                options={(routes.data?.data ?? []).map((route) => ({
                  value: route.id,
                  label: route.displayName,
                }))}
              />
            )}
            <FilterDate
              label="From"
              value={from}
              onChange={(value) => {
                setFrom(value);
                table.setPage(0);
              }}
            />
            <FilterDate
              label="To"
              value={to}
              onChange={(value) => {
                setTo(value);
                table.setPage(0);
              }}
            />
          </>
        }
        pagination={trips.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={trips.data?.data}
          rowKey={(row) => row.id}
          loading={trips.isPending}
          error={trips.error}
          onRetry={() => void trips.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => navigate(ROUTES.tripDetail(row.id))}
          emptyTitle="No trips"
          emptyDescription="Generate a day's trips, or schedule one from a route."
          emptyIcon={<Navigation className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={generateOpen}
        title="Generate a day's trips"
        description="One trip per staffed route and direction. Safe to run again: a slot that already has a live trip is counted, never duplicated."
        onClose={() => {
          setGenerateOpen(false);
          setGeneration(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setGenerateOpen(false);
                setGeneration(null);
              }}
            >
              Close
            </Button>
            <Button loading={generate.isPending} onClick={() => generate.mutate()}>
              Generate
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Date"
            type="date"
            required
            value={generateDate}
            onChange={(event) => setGenerateDate(event.target.value)}
          />

          {generation !== null && (
            <>
              <div className="border-2 border-line bg-surface px-4 py-3">
                <dl className="grid grid-cols-3 gap-x-4 text-[13px]">
                  <div>
                    <dt className="text-subtle">Created</dt>
                    <dd className="tabular-nums text-ink">{generation.created}</dd>
                  </div>
                  <div>
                    <dt className="text-subtle">Already scheduled</dt>
                    <dd className="tabular-nums text-ink">{generation.alreadyScheduled}</dd>
                  </div>
                  <div>
                    <dt className="text-subtle">Skipped</dt>
                    <dd className="tabular-nums text-ink">{generation.skipped.length}</dd>
                  </div>
                </dl>
              </div>

              {/* The routes that could not be filled, each with the server's
                  reason — never summarised into a bare count. */}
              <Blockers reasons={generation.skipped} title="Could not be scheduled" />
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

/** A compact date filter for the list toolbar, styled like FilterSelect. */
function FilterDate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted">
      <span className="text-xs uppercase tracking-[0.04em]">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink shadow-xs transition-colors hover:border-line-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
      />
    </label>
  );
}
